import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_CREATE_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import type { CreateClientFormData, EditClientData } from '@/types/clientsApiTypes';
import { nullableNumber, nullableString } from '@/utils/validations';
import { emailValidation } from '@/utils/validations';
import { CLIENT_DETAILS_QUERY_KEY, CLIENT_QUERY_KEY } from '../../../consts/client-detail';
import { EditEmergencyContactForm } from '../../EditSectionModals/components/EditEmergencyContactForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getEmergencyContactPayload } from '../../EditSectionModals/utils/getEmergencyContactPayload';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    client: yup.object().shape({
      emergency_contact_name: nullableString().optional(),
      emergency_relationship_type_id: nullableNumber().optional(),
      emergency_relationship: yup
        .object({
          label: yup.string().optional().defined(),
          value: yup.number().optional().nullable().defined(),
        })
        .nullable()
        .defined(),
      emergency_contact_phone: nullableString().optional(),
      emergency_contact_email: nullableString()
        .optional()
        .test('is-valid-email', 'Invalid email', (value) => !value || value.trim() === '' || emailValidation(value)),
    }),
  }),
});

export type EmergencyContactFormValues = yup.InferType<typeof schema>;

function useEmergencyContactOnSave() {
  const mutation = useEditClientMutation();
  const queryClient = useQueryClient();

  return async (payload: Partial<EditClientData>) => {
    await mutation.mutateAsync(
      {
        data: payload,
        clientId: Number(payload.client?.id),
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_QUERY_KEY] });
        },
      }
    );
  };
}

export const emergencyContactSection = defineSection<
  (typeof ModalKeys)['emergency_contact'],
  CreateClientFormData,
  typeof schema,
  Partial<EditClientData>
>({
  key: ModalKeys.emergency_contact,
  title: 'Emergency contact',
  subtitle: 'Designated contact to reach in case of an emergency involving the client.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: { client: c.primary_passenger.client },
  }),
  mapValuesToPayload: (v, client): Partial<EditClientData> => ({
    client: getEmergencyContactPayload({
      client_id: client?.primary_passenger.client.client_id ?? 0,
      ...v.primary_passenger.client,
    }),
  }),
  useOnSave: useEmergencyContactOnSave,
  Fields: EditEmergencyContactForm,
});